import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { Form, Spinner, Dropdown, Table, ProgressBar } from "react-bootstrap";
import { BiChevronLeft, BiCog, BiCalendar, BiPlus, BiMinus, BiSearch, BiFilterAlt, BiExport, BiChevronDown, BiDotsHorizontalRounded, BiPhoneCall, BiBot, BiBell } from "react-icons/bi";
import { FiArrowUpRight, FiArrowDownLeft } from "react-icons/fi";
import Layout from "layout/layout";
import routes from "routes";
import { useGetEnterprisePlanQuery, useUpdateEnterprisePlanMutation, useGetTenantOverviewQuery, useImpersonateTenantMutation } from "api/enterprisePlanApi";
import {
  EnterprisePlanResponseModel,
  TenantOverviewResponseData,
  TenantOverviewUser,
  TenantOverviewBreakdownItem,
  TenantOverviewTransaction
} from "model/enterprise-plan-model";
import { showToastError, showToastSuccess } from "components/toastify/ToastNotifications";
import { usePermissions } from "hooks/user-permissions";
import Chart from "react-apexcharts";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import "./CompanyDetails.css";
import taraImg from "assets/images/tara3.png";
import sophiaImg from "assets/images/sophia-blue.png";
import Pagination from "rc-pagination/lib/Pagination";
import { InviteEmployeeModal } from "../../components/InviteEmployeeModal";
const getInitials = (name?: string) => {
  if (!name) return "J"; // Default for Joble
  const words = name.trim().split(" ");
  if (words.length >= 2) { 
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  return words[0][0].toUpperCase();
};

const dummyActivityLogs = [
  {
    id: "log1",
    dateStr: "25th June 2024",
    secondaryDateStr: "22nd June 2024",
    logs: [
      { id: 1, type: "Support", user: "Thecla", action: "added phone credits for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 2, type: "Subscription", user: "Babatunde", action: "created an enterprise plan for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 3, type: "Support", user: "Thecla", action: "created phone agent - +238578483875 for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 4, type: "Support", user: "Thecla", action: "deactivated support agent - Bruce for Zarttech", time: "25th June 2024, 12:30pm WAT" },
    ]
  },
  {
    id: "log2",
    dateStr: "24th June 2024",
    secondaryDateStr: "22nd June 2024",
    logs: [
      { id: 1, type: "Support", user: "Thecla", action: "added phone credits for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 2, type: "Subscription", user: "Babatunde", action: "created an enterprise plan for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 3, type: "Support", user: "Thecla", action: "created phone agent - +238578483875 for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 4, type: "Support", user: "Thecla", action: "deactivated support agent - Bruce for Zarttech", time: "25th June 2024, 12:30pm WAT" },
    ]
  },
  {
    id: "log3",
    dateStr: "23rd June 2024",
    secondaryDateStr: "21st June 2024",
    logs: [
      { id: 1, type: "Support", user: "Thecla", action: "added phone credits for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 2, type: "Subscription", user: "Babatunde", action: "created an enterprise plan for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 3, type: "Support", user: "Thecla", action: "created phone agent - +238578483875 for Zarttech", time: "25th June 2024, 12:30pm WAT" },
      { id: 4, type: "Support", user: "Thecla", action: "deactivated support agent - Bruce for Zarttech", time: "25th June 2024, 12:30pm WAT" },
    ]
  }
];

const CompanyDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermissions } = usePermissions(["View Enterprise Plan", "Edit Enterprise Plan"]);

  const [selectedEnterprise, setSelectedEnterprise] = useState<EnterprisePlanResponseModel | null>(null);
  const [originalEnterprise, setOriginalEnterprise] = useState<EnterprisePlanResponseModel | null>(null);
  const [isEditMode, setIsEditMode] = useState(true); // Default to edit mode as per design
  const [hasChanges, setHasChanges] = useState(false);
  const [activeTab, setActiveTab] = useState("Transaction History");
  const [expandedLogs, setExpandedLogs] = useState<string[]>(['24th June 2024', '23rd June 2024']);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showInviteModal, setShowInviteModal] = useState(false);
  const dummySupportAgents = [
    { id: 1, name: "Musa", avatar: "https://i.pravatar.cc/150?u=musa", language: "English (US)", usecase: "Web Support", agentType: "Informative Agent", voice: "Male", outputPreview: "URL/Full Page", outputPreviewBg: "#E8DFFF", outputPreviewColor: "#9355FA", assignedTo: "15 Users", dateCreated: "October 15, 2025", status: "Active" },
    { id: 2, name: "Simbi", avatar: "https://i.pravatar.cc/150?u=simbi", language: "English (UK)", usecase: "Web Support", agentType: "Informative Agent", voice: "Female", outputPreview: "Small Widget", outputPreviewBg: "#F4DBDB", outputPreviewColor: "#D34747", assignedTo: "15 Users", dateCreated: "October 15, 2025", status: "Active" },
    { id: 3, name: "Bryan", avatar: "https://i.pravatar.cc/150?u=bryan", language: "French", usecase: "Web Support", agentType: "Booking Agent", voice: "Male", outputPreview: "Medium Widget", outputPreviewBg: "#FCE5CA", outputPreviewColor: "#E89B42", assignedTo: "0 Users", dateCreated: "October 15, 2025", status: "Inactive" }
  ];

  const dummyPhoneAgents = [
    { id: 1, phone: "(31) 24-3611111", country: "Netherlands", numberType: "Local Number", newOrExisting: "New", name: "Joble NL", prefText: "AI to Live Agent", prefBg: "#E8DFFF", prefColor: "#9355FA", caps: ["Voice", "AI"], assignedTo: "15 Users", status: "Active" },
    { id: 2, phone: "(31) 24-3611111", country: "Netherlands", numberType: "Toll-free number", newOrExisting: "Existing", name: "Joble NG", prefText: "Live Agent to AI", prefBg: "#F4DBDB", prefColor: "#D34747", caps: ["Voice"], assignedTo: "15 Users", status: "Active" },
    { id: 3, phone: "(31) 24-3611111", country: "Netherlands", numberType: "Mobile number", newOrExisting: "New", name: "Joble AF", prefText: "AI to Live Agent", prefBg: "#E8DFFF", prefColor: "#9355FA", caps: ["Voice", "AI"], assignedTo: "0 Users", status: "Inactive" }
  ];

  const toggleLog = (dateStr: string) => {
    if (expandedLogs.includes(dateStr)) {
      setExpandedLogs(expandedLogs.filter(d => d !== dateStr));
    } else {
      setExpandedLogs([...expandedLogs, dateStr]);
    }
  };

  const onPageChange = (page: number) => {
    setCurrentPage(page);
  };

  const [updateEnterprise, { isLoading: updating }] = useUpdateEnterprisePlanMutation();

  const { state } = useLocation();
  const subdomain = state?.subdomain;

  const [impersonateTenant, { isLoading: isImpersonating }] = useImpersonateTenantMutation();

  const handleImpersonate = async () => {
    const targetSubdomain = subdomain || selectedEnterprise?.tenantId;
    if (!targetSubdomain) {
      showToastError("Subdomain not found");
      return;
    }
    try {
      const result = await impersonateTenant(targetSubdomain).unwrap();
      if (!result?.Code) {
        showToastError("Failed to login to company workspace");
        return;
      }

      let targetUrl = "";
      if (window.location.hostname === "localhost") {
        targetUrl = `http://localhost:42332/impersonate?code=${encodeURIComponent(result.Code)}`;
      } else if (window.location.hostname.includes("pactocoin.com")) {
        targetUrl = `https://${targetSubdomain}.joble.pactocoin.com/impersonate?code=${encodeURIComponent(result.Code)}`;
      } else {
        targetUrl = `https://${targetSubdomain}.app.joble.app/impersonate?code=${encodeURIComponent(result.Code)}`;
      }
      window.open(targetUrl, "_blank");
    } catch (error) {
      showToastError("Failed to login to company workspace");
    }
  };

  const { data: tenantOverview, isFetching: loadingOverview } = useGetTenantOverviewQuery(
    subdomain || selectedEnterprise?.tenantId || "",
    { skip: !(subdomain || selectedEnterprise?.tenantId) }
  );

  const overviewData = (tenantOverview?.data?.Data || (tenantOverview as any)?.Data || (tenantOverview as any)?.data) as TenantOverviewResponseData | undefined;
  console.log("CompanyDetails Render - subdomain:", subdomain, "selectedSubdomain/tenantId:", selectedEnterprise?.tenantId);
  console.log("CompanyDetails Render - tenantOverview response:", tenantOverview);
  console.log("CompanyDetails Render - extracted overviewData:", overviewData);
  const companyData = overviewData?.Company;

  // The plan/quota search only matches CompanyName/AdminEmail/CompanyType/Location
  // server-side (see SubscriptionServices.ApplySearchKeyword in jobsuite-backend) — it
  // never matches by enterpriseSubId. Searching by the GUID route param returned no
  // results, so selectedEnterprise never got storage/communication/project/data-retention
  // fields, and every save failed EditEnterpricePlan's validation for those fields
  // (they default to 0/false when absent from the request body). Search by company name
  // instead, once we know it from the tenant overview, then still pick the exact record
  // by enterpriseSubId below.
  const { data: enterprisePlan, isFetching: loadingPlan } = useGetEnterprisePlanQuery(
    {
      pageNumber: 1,
      pageSize: 100,
      searchKeyword: companyData?.CompanyName || "",
    },
    { skip: !companyData?.CompanyName }
  );

  const phoneSub = overviewData?.PhoneAgentSubscription;
  const supportSub = overviewData?.SupportAgentSubscription;
  const usage = overviewData?.Usage;
  const callBreakdown = overviewData?.CallMinutesBreakdown || [];
  const supportBreakdown = overviewData?.SupportAgentMinutesBreakdown || [];
  const seatDistribution = overviewData?.SeatDistribution || [];
  const supportPlan = overviewData?.SupportPlan;
  const transactions = overviewData?.TransactionHistory || [];

  useEffect(() => {
    let baseItem: Partial<EnterprisePlanResponseModel> = {};
    if (enterprisePlan?.data?.items) {
      const found = enterprisePlan.data.items.find((i: EnterprisePlanResponseModel) => i.enterpriseSubId === id);
      if (found) {
        baseItem = found;
      }
    }

    if (Object.keys(baseItem).length > 0 || companyData) {
      const mergedItem = {
        ...baseItem,
        companyName: companyData?.CompanyName || baseItem.companyName || "",
        adminEmail: companyData?.AdminEmail || baseItem.adminEmail || "",
        adminName: companyData?.AdminName || baseItem.adminName || "",
        phoneNumber: companyData?.PhoneNumber || baseItem.phoneNumber || "",
        alternatePhoneNumber: companyData?.AlternatePhoneNumber || baseItem.alternatePhoneNumber || "",
        maxNumberofUser: companyData?.NumberOfPurchasedLicenses ?? baseItem.maxNumberofUser ?? 0,
        subscriptionType: companyData?.SubscriptionType || baseItem.subscriptionType || "",
        logo: companyData?.LogoUrl || baseItem.logo || "",
        location: companyData?.Country || baseItem.location || "",
        enterpriseSubId: id,
        tenantId: companyData?.Subdomain || baseItem.tenantId || "",
      };
      setSelectedEnterprise(mergedItem as EnterprisePlanResponseModel);
      setOriginalEnterprise(mergedItem as EnterprisePlanResponseModel);
    }
  }, [enterprisePlan, id, companyData]);

  const handleFieldChange = (field: keyof EnterprisePlanResponseModel, value: any) => {
    if (selectedEnterprise) {
      const updated = { ...selectedEnterprise, [field]: value };
      setSelectedEnterprise(updated);
      setHasChanges(JSON.stringify(updated) !== JSON.stringify(originalEnterprise));
    }
  };

  const handleSave = async () => {
    if (!selectedEnterprise) return;
    try {
      const response = await updateEnterprise(selectedEnterprise).unwrap();
      if (response.ResponseCode === "00") {
        showToastSuccess("Enterprise plan updated successfully");
        setOriginalEnterprise(selectedEnterprise);
        setHasChanges(false);
      } else {
        showToastError(response.ResponseMessage || "Failed to update enterprise plan");
      }
    } catch (error) {
      showToastError("An error occurred while updating enterprise plan");
    }
  };

  const donutOptions = (colors: string[]) => ({
    chart: { type: "donut" as const },
    colors: colors,
    legend: { show: false },
    dataLabels: { enabled: false },
    plotOptions: {
      pie: {
        donut: {
          size: "75%",
          labels: {
            show: true,
            total: {
              show: true,
              label: "Total Mins",
              formatter: () => "23,000",
            }
          }
        }
      }
    }
  });

  const sparklineOptions = (color: string) => ({
    chart: { type: "area" as const, sparkline: { enabled: true } },
    stroke: { curve: "smooth" as const, width: 2 },
    colors: [color],
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0,
        stops: [0, 100]
      }
    },
    tooltip: { enabled: false }
  });

  const dummySparkData = [30, 40, 35, 50, 49, 60, 70, 91, 125];

  const filteredTransactions = transactions.filter((t: any) => 
    t.Description?.toLowerCase().includes(searchKeyword.toLowerCase()) || 
    t.Reference?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    t.TransactionType?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const supportAgents = overviewData?.SupportAgents || [];
  const filteredSupportAgents = supportAgents.filter(a => 
    a.Name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.Language?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.WidgetType?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const phoneAgents = overviewData?.PhoneAgents || [];
  const filteredPhoneAgents = phoneAgents.filter(a => 
    a.Name?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.CallPreference?.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.Language?.toLowerCase().includes(searchKeyword.toLowerCase())
  );

  const activityLogs = overviewData?.ActivityLogs || [];
  
  const groupedActivityLogs = activityLogs.reduce((acc, log) => {
    const dateObj = new Date(log.CreatedAt);
    const dateStr = dateObj.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    if (!acc[dateStr]) acc[dateStr] = [];
    acc[dateStr].push(log);
    return acc;
  }, {} as Record<string, typeof activityLogs>);
  
  const formattedActivityLogs = Object.entries(groupedActivityLogs).map(([dateStr, logs]) => ({
    id: dateStr,
    dateStr,
    secondaryDateStr: dateStr,
    logs: logs.map(l => ({
      id: l.Id,
      type: l.EventCategory,
      user: l.By,
      action: l.ActivitySummary || l.Description,
      time: new Date(l.CreatedAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
    }))
  }));

  const filteredActivityLogs = formattedActivityLogs.filter(a => 
    a.dateStr.toLowerCase().includes(searchKeyword.toLowerCase()) ||
    a.logs.some(l => l.action?.toLowerCase().includes(searchKeyword.toLowerCase()) || l.user?.toLowerCase().includes(searchKeyword.toLowerCase()))
  );

  if (loadingPlan && !selectedEnterprise) {
    return (
      <Layout>
        <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
          <Spinner animation="border" variant="primary" />
        </div>
      </Layout>
    );
  }

  return (
    <div className="details-page">
      <Layout>
        <div className="details-header d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-3">
            <button onClick={() => navigate(routes.EnterprisePlan)} className="btn btn-light rounded-circle p-2">
              <BiChevronLeft size={24} />
            </button>
            <div className="d-flex align-items-center gap-2">
              <div className="bg-primary rounded-circle d-flex align-items-center justify-content-center text-white fw-bold" style={{ width: "41px", height: "41px", fontSize: "19px" }}>
                {selectedEnterprise?.logo ? (
                  <img src={selectedEnterprise.logo} alt="logo" style={{ width: "41px", height: "41px", borderRadius: "50%", objectFit: "cover" }} />
                ) : (
                  getInitials(selectedEnterprise?.companyName || "Joble")
                )}
              </div>
              <h5 className="mb-0 fw-bold">{selectedEnterprise?.companyName || "Joble"}</h5>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <button 
              className="btn btn-primary px-4 py-2 rounded-2 w-70" 
              style={{ backgroundColor: "#008CFE", borderColor: "#008CFE" }}
              onClick={handleImpersonate}
              disabled={isImpersonating}
            >
              {isImpersonating ? <Spinner size="sm" animation="border" /> : "Login to Company"}
            </button>
            {/* <button className="btn btn-light rounded-pill p-2"><BiCog size={20} /></button>
            <button className="btn btn-light rounded-pill p-2"><BiCalendar size={20} /></button> */}
          </div>
        </div>

        <div className="container-fluid py-4 px-4">
          {/* Form Section */}
          <div className="form-section shadow-sm border">
            <div className="row g-4">
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Company Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={selectedEnterprise?.companyName || ""} 
                    className="form-control-custom"
                    readOnly
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Phone number</Form.Label>
                    <PhoneInput
                      international
                      defaultCountry="NL"
                      value={selectedEnterprise?.phoneNumber || ""}
                      onChange={(v?: string) => handleFieldChange("phoneNumber", v)}
                      className="w-100 px-3 "
                    />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Alternate Phone number</Form.Label>
                    <PhoneInput
                      international
                      defaultCountry="NL"
                      value={selectedEnterprise?.alternatePhoneNumber || ""}
                      onChange={(v?: string) => handleFieldChange("alternatePhoneNumber", v)}
                      className="w-100 px-3"
                    />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Admin Email</Form.Label>
                  <Form.Control 
                    type="email" 
                    value={selectedEnterprise?.adminEmail || ""} 
                    onChange={(e) => handleFieldChange("adminEmail", e.target.value)}
                    className="form-control-custom"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Admin Name</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={selectedEnterprise?.adminName || ""} 
                    onChange={(e) => handleFieldChange("adminName", e.target.value)}
                    className="form-control-custom"
                  />
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Number of Purchased Licenses</Form.Label>
                  <div className="form-control-custom d-flex align-items-center justify-content-between px-3">
                    <span>{selectedEnterprise?.maxNumberofUser || 50}</span>
                    <div className="license-control bg-transparent p-0">
                      <button className="license-btn" onClick={() => handleFieldChange("maxNumberofUser", (selectedEnterprise?.maxNumberofUser || 0) - 1)}><BiMinus /></button>
                      <button className="license-btn" onClick={() => handleFieldChange("maxNumberofUser", (selectedEnterprise?.maxNumberofUser || 0) + 1)}><BiPlus /></button>
                    </div>
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Subscription Type</Form.Label>
                  <Form.Select 
                    value={selectedEnterprise?.subscriptionType || "Enterprise"}
                    onChange={(e) => handleFieldChange("subscriptionType", e.target.value)}
                    className="form-control-custom form-select-custom" 
                  >
                    <option value="Enterprise">Enterprise</option>
                    <option value="Pro">Pro</option>
                    <option value="Starter">Starter</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-md-4">
                <Form.Group>
                  <Form.Label className="form-label-custom">Users</Form.Label>
                  <div className="form-control-custom d-flex align-items-center justify-content-between px-3">
                    <div className="avatar-stack d-flex align-items-center">
                      {companyData?.Users?.slice(0, 3).map((u: TenantOverviewUser, i: number) => (
                        <div
                          key={u.UserId}
                          title={u.FullName}
                          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
                          style={{
                            width: "24px",
                            height: "24px",
                            marginLeft: i > 0 ? "-6px" : "0",
                            border: "1px solid white",
                            fontSize: "10px",
                            zIndex: 3 - i,
                            backgroundImage: u.AvatarUrl ? `url(${u.AvatarUrl})` : "none",
                            backgroundSize: "cover"
                          }}
                        >
                          {!u.AvatarUrl && getInitials(u.FullName)}
                        </div>
                      ))}
                      {companyData?.Users && companyData.Users.length > 3 && (
                        <div className="rounded-circle bg-secondary text-white small d-flex align-items-center justify-content-center" style={{ width: "24px", height: "24px", marginLeft: "-6px", border: "1px solid white", fontSize: "10px", zIndex: 0 }}>
                          +{companyData.Users.length - 3}
                        </div>
                      )}
                    </div>
                    <button type="button" onClick={() => setShowInviteModal(true)} className="btn btn-primary btn-sm rounded-pill px-3" style={{ fontSize: "10px" }}>Add User</button>
                  </div>
                </Form.Group>
              </div>
              <div className="col-md-4 d-flex align-items-end">
                <button 
                  onClick={handleSave} 
                  className="btn btn-primary w-100 py-3 fw-bold"
                  style={{ backgroundColor: "#008CFE", borderColor: "#008CFE", borderRadius: "32px" }}
                >
                  {updating ? <Spinner size="sm" /> : "Save"}
                </button>
              </div>
            </div>
          </div>

          {/* Subscriptions Section */}
          <div className="row g-4 mb-4">
            <div className="col-md-6">
              <div className="custom-card subscription-card phone bg-white p-4" style={{ borderRadius: "24px" }}>
                <div className="row g-4">
                  {/* Left Column */}
                  <div className="col-md-5 d-flex flex-column justify-content-between">
                    <div>
                      <p   className="mb-4" style={{ color: "#8C1AFF", fontSize: "17px" }}>Phone Agent Subscription</p>
                      <div className="d-flex align-items-center mb-4">
                        <div>
                          <img src={taraImg} className="rounded-circle w-100 h-100" style={{ objectFit: "cover" }} alt="tara" />
                        </div>
                        <div>
                          <h4 className="fw-bold text-dark mb-2">{phoneSub?.PlanName || "Starter"}</h4>
                          <span className="badge text-dark px-3 py-2 fw-medium" style={{ backgroundColor: "#BDBDBD", borderRadius: "6px" }}>Current Plan</span>
                        </div>
                      </div>
                    </div>
                    <button className="btn btn-purple w-100 rounded-pill py-2 shadow-sm" style={{ backgroundColor: "#8C1AFF", borderColor: "#A855F7", color: "white" }}>Change Plan</button>
                  </div>

                  {/* Right Column */}
                  <div className="col-md-7">
                    <div className="d-flex flex-column gap-3 h-100 justify-content-between">
                      {/* Top Box */}
                      <div className="bg-white shadow-sm position-relative overflow-hidden p-3 pt-4" style={{ border: "1px solid #f3f4f6" }}>
                        <h6 className="text-dark mb-3 fw-bold">Phone Credit Balance</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="fw-bold mb-0" style={{ color: "#A855F7" }}>
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: phoneSub?.Currency || 'EUR' }).format(phoneSub?.CreditBalance || 0)}
                          </h6>
                          <button className="btn rounded-pill px-4 py-1 fw-medium" style={{ border: "1px solid #A855F7", color: "#A855F7", fontSize: "14px", backgroundColor: "transparent" }}>Topup Credit</button>
                        </div>
                        <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px" }}>
                          <div className="h-100" style={{ width: "100%", backgroundColor: "#A855F7" }}></div>
                        </div>
                      </div>

                      {/* Bottom Box */}
                      <div className="bg-white shadow-sm position-relative overflow-hidden p-3 pt-4" style={{ border: "1px solid #f3f4f6" }}>
                        <h6 className="fw-bold text-dark mb-3">Total Phone Agents</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="fw-bold mb-0" style={{ color: "#A855F7" }}>{phoneSub?.TotalAgents || 0}</h6>
                          <button className="btn rounded-pill px-4 py-1 fw-medium" style={{ border: "1px solid #A855F7", color: "#A855F7", fontSize: "14px", backgroundColor: "transparent" }}>Add Phone Agent</button>
                        </div>
                        <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px" }}>
                          <div className="h-100" style={{ width: "100%", backgroundColor: "#A855F7" }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="custom-card subscription-card support bg-white p-4" style={{ borderRadius: "24px" }}>
                <div className="row g-4">
                  {/* Left Column */}
                  <div className="col-md-5 d-flex flex-column justify-content-between">
                    <div>
                      <p className="mb-4" style={{ color: "#06b6d4", fontSize: "17px" }}>Support Agent Subscription</p>
                      <div className="d-flex align-items-center mb-4">
                        <div>
                          <img src={sophiaImg} className="rounded-circle w-100 h-100" style={{ objectFit: "cover" }} alt="sophia" />
                        </div>
                        <div>
                          <h4 className="fw-bold text-dark mb-2">{supportSub?.PlanName || "Starter"}</h4>
                          <span className="badge text-dark px-3 py-2 fw-medium" style={{ backgroundColor: "#BDBDBD", borderRadius: "6px" }}>Current Plan</span>
                        </div>
                      </div>
                    </div>
                    <button className="btn btn-cyan w-100 rounded-pill py-2 shadow-sm" style={{ backgroundColor: "#06b6d4", borderColor: "#06b6d4", color: "white" }}>Change Plan</button>
                  </div>

                  {/* Right Column */}
                  <div className="col-md-7">
                    <div className="d-flex flex-column gap-3 h-100 justify-content-between">
                      {/* Top Box */}
                      <div className="bg-white shadow-sm position-relative overflow-hidden p-3 pt-4" style={{ border: "1px solid #f3f4f6" }}>
                        <h6 className="text-dark mb-3 fw-bold">Support Credit Balance</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="fw-bold mb-0" style={{ color: "#06b6d4" }}>
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: supportSub?.Currency || 'EUR' }).format(supportSub?.CreditBalance || 0)}
                          </h6>
                          <button className="btn rounded-pill px-4 py-1 fw-medium" style={{ border: "1px solid #06b6d4", color: "#06b6d4", fontSize: "14px", backgroundColor: "transparent" }}>Topup Credit</button>
                        </div>
                        <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px" }}>
                          <div className="h-100" style={{ width: "100%", backgroundColor: "#06b6d4" }}></div>
                        </div>
                      </div>

                      {/* Bottom Box */}
                      <div className="bg-white shadow-sm position-relative overflow-hidden p-3 pt-4" style={{ border: "1px solid #f3f4f6" }}>
                        <h6 className="fw-bold text-dark mb-3">Total Support Agents</h6>
                        <div className="d-flex justify-content-between align-items-center">
                          <h6 className="fw-bold mb-0" style={{ color: "#06b6d4" }}>{supportSub?.TotalAgents || 0}</h6>
                          <button className="btn rounded-pill px-4 py-1 fw-medium" style={{ border: "1px solid #06b6d4", color: "#06b6d4", fontSize: "14px", backgroundColor: "transparent" }}>Add Support Agent</button>
                        </div>
                        <div className="position-absolute bottom-0 start-0 w-100" style={{ height: "4px" }}>
                          <div className="h-100" style={{ width: "100%", backgroundColor: "#06b6d4" }}></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Metrics Grid */}
          <div className="row g-4 mb-4">
            {[
              { label: "Phone Agent Minutes Usage", val: usage?.PhoneAgentMinutesUsage?.Value || 0, change: usage?.PhoneAgentMinutesUsage?.WeekChangePercent || 0, trend: usage?.PhoneAgentMinutesUsage?.Trend || "flat", color: "#8C1AFF", theme: "purple" },
              { label: "Meetings Booked By Phone Agent", val: usage?.MeetingsBookedByPhoneAgent?.Value || 0, change: usage?.MeetingsBookedByPhoneAgent?.WeekChangePercent || 0, trend: usage?.MeetingsBookedByPhoneAgent?.Trend || "flat", color: "#8C1AFF", theme: "purple" },
              { label: "Todos Created By Phone Agent", val: usage?.TodosCreatedByPhoneAgent?.Value || 0, change: usage?.TodosCreatedByPhoneAgent?.WeekChangePercent || 0, trend: usage?.TodosCreatedByPhoneAgent?.Trend || "flat", color: "#8C1AFF", theme: "purple" },
              { label: "Surveys Answered By Phone Agent", val: usage?.SurveysAnsweredByPhoneAgent?.Value || 0, change: usage?.SurveysAnsweredByPhoneAgent?.WeekChangePercent || 0, trend: usage?.SurveysAnsweredByPhoneAgent?.Trend || "flat", color: "#8C1AFF", theme: "purple" },
              { label: "Support Agent Minutes Usage", val: usage?.SupportAgentMinutesUsage?.Value || 0, change: usage?.SupportAgentMinutesUsage?.WeekChangePercent || 0, trend: usage?.SupportAgentMinutesUsage?.Trend || "flat", color: "#00E5FF", theme: "cyan" },
              { label: "Meetings Booked By Support Agent", val: usage?.MeetingsBookedBySupportAgent?.Value || 0, change: usage?.MeetingsBookedBySupportAgent?.WeekChangePercent || 0, trend: usage?.MeetingsBookedBySupportAgent?.Trend || "flat", color: "#00E5FF", theme: "cyan" },
              { label: "Todos Created By Support Agent", val: usage?.TodosCreatedBySupportAgent?.Value || 0, change: usage?.TodosCreatedBySupportAgent?.WeekChangePercent || 0, trend: usage?.TodosCreatedBySupportAgent?.Trend || "flat", color: "#00E5FF", theme: "cyan" },
              { label: "Accounts Created By Support Agent", val: usage?.AccountsCreatedBySupportAgent?.Value || 0, change: usage?.AccountsCreatedBySupportAgent?.WeekChangePercent || 0, trend: usage?.AccountsCreatedBySupportAgent?.Trend || "flat", color: "#00E5FF", theme: "cyan" }
            ].map((m, idx) => (
              <div key={idx} className="col-md-3">
                <div className={`metric-box ${m.theme}`}>
                  <p className="small fw-bold text-dark mb-1">{m.label}</p>
                  <div className="d-flex justify-content-between align-items-end mt-2">
                    <div>
                      <h2 className="fw-bold text-dark mb-1">{new Intl.NumberFormat('en-US').format(m.val)}</h2>
                      <div className="stat-change" style={{ color: m.trend === 'down' ? '#EB5757' : (m.trend === 'up' ? '#219653' : '#828282'), fontSize: '13px', fontWeight: 500 }}>
                        {m.trend === 'down' ? <FiArrowDownLeft /> : (m.trend === 'up' ? <FiArrowUpRight /> : "")} {Math.abs(m.change)}% this week
                      </div>
                    </div>
                    <div style={{ width: "80px", height: "40px" }}>
                      <Chart options={sparklineOptions(m.color)} series={[{ data: dummySparkData }]} type="area" height={50} />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Analytics Section */}
          <div className="row g-4 mb-4">
            <div className="col-md-6">
              <div className="custom-card p-4 border-purple border-bottom border-5 border-top-0 border-start-0 border-end-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <Chart 
                      options={donutOptions(["#0C1F56", "#F2994A", "#EB5757", "#219653"])} 
                      series={callBreakdown.length > 0 ? callBreakdown.map((b: TenantOverviewBreakdownItem) => b.Value) : [1]} 
                      type="donut" 
                      height={200} 
                    />
                  </div>
                  <div className="col-7">
                    <div className="chart-legend">
                      {callBreakdown.map((b: TenantOverviewBreakdownItem, i: number) => (
                        <div key={i} className="chart-legend-item">
                          <span className="legend-dot" style={{ background: ["#0C1F56", "#F2994A", "#EB5757", "#219653"][i % 4] }}></span> #{i + 1}. {b.Label} - {new Intl.NumberFormat('en-US').format(b.Value)} Mins
                        </div>
                      ))}
                      {callBreakdown.length === 0 && <div className="text-muted small">No data available</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="custom-card p-4 border-cyan border-bottom border-5 border-top-0 border-start-0 border-end-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <Chart 
                      options={donutOptions(["#0C1F56", "#F2994A"])} 
                      series={supportBreakdown.length > 0 ? supportBreakdown.map((b: TenantOverviewBreakdownItem) => b.Value) : [1]} 
                      type="donut" 
                      height={200} 
                    />
                  </div>
                  <div className="col-7">
                    <div className="chart-legend">
                      {supportBreakdown.map((b: TenantOverviewBreakdownItem, i: number) => (
                        <div key={i} className="chart-legend-item">
                          <span className="legend-dot" style={{ background: ["#0C1F56", "#F2994A"][i % 2] }}></span> #{i + 1}. {b.Label} - {new Intl.NumberFormat('en-US').format(b.Value)} Mins
                        </div>
                      ))}
                      {supportBreakdown.length === 0 && <div className="text-muted small">No data available</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row g-4">
            <div className="col-md-6">
              <div className="custom-card p-4 border-blue border-bottom border-5 border-top-0 border-start-0 border-end-0">
                <div className="row align-items-center">
                  <div className="col-5">
                    <Chart 
                      options={donutOptions(["#219653", "#3b82f6", "#a855f7"])} 
                      series={seatDistribution.length > 0 ? seatDistribution.map((b: TenantOverviewBreakdownItem) => b.Value) : [1]} 
                      type="donut" 
                      height={200} 
                    />
                  </div>
                  <div className="col-7">
                    <div className="chart-legend">
                      {seatDistribution.map((b: TenantOverviewBreakdownItem, i: number) => (
                        <div key={i} className="chart-legend-item">
                          <span className="legend-dot" style={{ background: ["#219653", "#3b82f6", "#a855f7"][i % 3] }}></span> #{i + 1}. {b.Label} - {b.Percentage}%
                        </div>
                      ))}
                      {seatDistribution.length === 0 && <div className="text-muted small">No data available</div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="custom-card p-4 border-blue border-bottom border-5 border-top-0 border-start-0 border-end-0">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <p className="small fw-bold mb-1">Support Plan</p>
                    <h4 className="fw-bold text-primary mb-0">{supportPlan?.PlanName || "Developer Support"}</h4>
                  </div>
                  <button className="btn btn-primary rounded-pill px-4">Change Plan</button>
                </div>
                <p className="text-muted small">{supportPlan?.Description || "Access to ticket-based assistance on a best effort basis, without guaranteed response times."}</p>
              </div>
            </div>
          </div>

          {/* Bottom Tabs */}
          <div className="mt-5 p-4">
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
              <div className="d-flex flex-wrap gap-2 gap-md-3">
                {["Transaction History", "Phone Agents", "Support Agents", "Activity Log"].map(tab => (
                  <button 
                    key={tab} 
                    type="button"
                    className={`${activeTab === tab ? "btn-primary-outline" : "btn-secondary-outline"} p-2 fw-medium`}
                    onClick={() => setActiveTab(tab)}
                    style={{ borderRadius: '10px' }}
                  >
                    {tab}
                  </button>
                ))}
              </div>
              
              <div className="d-flex gap-3 align-items-center mt-3 mt-md-0">
                <div className="d-flex light-text bg-white border rounded-4 px-2">
                  <Form.Control
                    type="text"
                    placeholder="Search"
                    className="border-0 px-2 search-input light-text bg-transparent shadow-none"
                    style={{ minWidth: '200px' }}
                    value={searchKeyword}
                    onChange={(e) => {
                      setSearchKeyword(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                  <button type="button" className="btn text-secondary p-0 px-1">
                    <BiSearch size={20} className="my-auto" />
                  </button>
                </div>
                <button className="btn-secondary-outline p-2 d-flex align-items-center gap-2 fw-medium" style={{ borderRadius: '10px' }}>
                  <BiExport /> Export
                </button>
                {/* <button className="btn-secondary-outline p-2 d-flex align-items-center gap-2 fw-medium" style={{ borderRadius: '10px' }}>
                  <BiFilterAlt /> Filter
                </button> */}
                <button className="btn-secondary-outline p-2 d-flex align-items-center" style={{ borderRadius: '10px' }}>
                  <BiCalendar size={18} />
                </button>
              </div>
            </div>

            {activeTab === "Transaction History" && (
              <>
              <Table responsive className="table mt-4 table-borderless align-middle" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                <thead className="border-bottom">
                  <tr>
                    <th className="py-3">
                      <label className="fw-medium text-muted small d-flex align-items-center gap-1">SN <BiChevronDown /></label>
                    </th>
                    <th className="py-3">
                      <label className="fw-medium text-muted small d-flex align-items-center gap-1">Date <BiChevronDown /></label>
                    </th>
                    <th className="py-3">
                      <label className="fw-medium text-muted small d-flex align-items-center gap-1">Trans Type <BiChevronDown /></label>
                    </th>
                    <th className="py-3">
                      <label className="fw-medium text-muted small d-flex align-items-center gap-1">Amount <BiChevronDown /></label>
                    </th>
                    <th className="py-3">
                      <label className="fw-medium text-muted small d-flex align-items-center gap-1">Description <BiChevronDown /></label>
                    </th>
                    <th className="py-3">
                      <label className="fw-medium text-muted small d-flex align-items-center gap-1">Duration <BiChevronDown /></label>
                    </th>
                    <th className="py-3">
                      <label className="fw-medium text-muted small d-flex align-items-center gap-1">Reference <BiChevronDown /></label>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length > 0 ? filteredTransactions.slice((currentPage - 1) * 10, currentPage * 10).map((row: TenantOverviewTransaction, i: number) => (
                    <tr key={i} className="border-bottom">
                      <td className="py-3 fw-bold text-dark">{(currentPage - 1) * 10 + i + 1}.</td>
                      <td className="py-3 text-dark fw-medium" style={{ fontSize: '14px' }}>
                        {new Date(row.Date).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className={`py-3 fw-medium ${row.TransactionType === 'Credit' ? 'text-success' : 'text-danger'}`} style={{ fontSize: '14px' }}>{row.TransactionType}</td>
                      <td className="py-3 text-dark fw-medium" style={{ fontSize: '14px' }}>
                        {new Intl.NumberFormat('en-US', { style: 'currency', currency: row.Currency || 'EUR' }).format(row.Amount)}
                      </td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{row.Description}</td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{row.Duration}</td>
                      <td className="py-3 text-muted" style={{ fontSize: '14px' }}>{row.Reference}</td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={7} className="py-5 text-center text-muted">No transactions found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <div className="text-end py-3">
                <Pagination
                  current={currentPage}
                  total={filteredTransactions.length > 0 ? filteredTransactions.length : 50}
                  pageSize={10}
                  onChange={onPageChange}
                />
              </div>
              </>
            )}
            
            {activeTab === "Support Agents" && (
              <>
              <Table responsive className="table mt-4 table-borderless align-middle" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                <thead className="border-bottom">
                  <tr>
                    <th className="py-3 text-muted fw-medium small">SN</th>
                    <th className="py-3 text-muted fw-medium small">Agent Name</th>
                    <th className="py-3 text-muted fw-medium small">Language</th>
                    <th className="py-3 text-muted fw-medium small">Usecase</th>
                    <th className="py-3 text-muted fw-medium small">Agent Type</th>
                    <th className="py-3 text-muted fw-medium small">Voice</th>
                    <th className="py-3 text-muted fw-medium small">Output Preview</th>
                    <th className="py-3 text-muted fw-medium small">Assigned to</th>
                    <th className="py-3 text-muted fw-medium small">Date Created</th>
                    <th className="py-3 text-muted fw-medium small">Status</th>
                    <th className="py-3 text-muted fw-medium small"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSupportAgents.slice((currentPage - 1) * 10, currentPage * 10).map((agent, index) => (
                    <tr key={agent.Id} className="border-bottom">
                      <td className="py-3 fw-bold text-dark">{(currentPage - 1) * 10 + index + 1}.</td>
                      <td className="py-3 text-dark fw-medium d-flex align-items-center gap-2" style={{ fontSize: '14px' }}>
                        <div className="rounded-circle bg-secondary d-flex justify-content-center align-items-center text-white" style={{ width: '30px', height: '30px', objectFit: 'cover' }}>{getInitials(agent.Name)}</div>
                        {agent.Name}
                      </td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{agent.Language || "N/A"}</td>
                      <td className="py-3 text-dark fw-medium" style={{ fontSize: '14px' }}>{agent.WidgetType || "N/A"}</td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{agent.WidgetType || "N/A"}</td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{agent.VoiceName || "N/A"}</td>
                      <td className="py-3" style={{ fontSize: '14px' }}>
                        <span className="badge rounded-pill px-3 py-2 fw-medium" style={{ backgroundColor: "#E8DFFF", color: "#9355FA" }}>
                          {agent.WidgetType || "Widget"}
                        </span>
                      </td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>N/A</td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{new Date(agent.CreatedAt).toLocaleDateString()}</td>
                      <td className={`py-3 fw-medium ${agent.Status === 'Active' ? 'text-success' : 'text-danger'}`} style={{ fontSize: '14px' }}>{agent.Status || "Active"}</td>
                      <td className="py-3 text-muted">
                        <button className="btn btn-link text-muted p-0"><BiDotsHorizontalRounded size={20} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="text-end py-3">
                <Pagination
                  current={currentPage}
                  total={filteredSupportAgents.length || 50}
                  pageSize={10}
                  onChange={onPageChange}
                />
              </div>
              </>
            )}

            {activeTab === "Phone Agents" && (
              <>
              <Table responsive className="table mt-4 table-borderless align-middle" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                <thead className="border-bottom">
                  <tr>
                    <th className="py-3 text-muted fw-medium small">SN</th>
                    <th className="py-3 text-muted fw-medium small">Phone Number</th>
                    <th className="py-3 text-muted fw-medium small">Country</th>
                    <th className="py-3 text-muted fw-medium small">Number Type</th>
                    <th className="py-3 text-muted fw-medium small">New or existing</th>
                    <th className="py-3 text-muted fw-medium small">Name</th>
                    <th className="py-3 text-muted fw-medium small">Call Preference</th>
                    <th className="py-3 text-muted fw-medium small">Capabilities</th>
                    <th className="py-3 text-muted fw-medium small">Assigned to</th>
                    <th className="py-3 text-muted fw-medium small">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPhoneAgents.slice((currentPage - 1) * 10, currentPage * 10).map((agent, index) => (
                    <tr key={agent.Id} className="border-bottom">
                      <td className="py-3 fw-bold text-dark">{(currentPage - 1) * 10 + index + 1}.</td>
                      <td className="py-3 text-dark fw-medium" style={{ fontSize: '14px' }}>{agent.PhoneNumber || "N/A"}</td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{agent.Country || "N/A"}</td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{agent.NumberType || "N/A"}</td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{agent.NewOrExisting || "N/A"}</td>
                      <td className="py-3 text-dark fw-medium" style={{ fontSize: '14px' }}>{agent.Name}</td>
                      <td className="py-3" style={{ fontSize: '14px' }}>
                        <span className="badge rounded-pill px-3 py-2 fw-medium" style={{ backgroundColor: "#E8DFFF", color: "#9355FA" }}>
                          {agent.CallPreference}
                        </span>
                      </td>
                      <td className="py-3">
                        <div className="d-flex flex-column gap-1">
                          <div className="text-primary small fw-bold d-flex align-items-center gap-1"><BiPhoneCall size={16}/> Voice</div>
                          <div className="text-primary small fw-bold d-flex align-items-center gap-1"><BiBot size={16}/> AI</div>
                        </div>
                      </td>
                      <td className="py-3 text-muted fw-medium" style={{ fontSize: '14px' }}>{agent.AssignedTo || "N/A"}</td>
                      <td className={`py-3 fw-medium ${agent.Status === 'Active' ? 'text-success' : 'text-danger'}`} style={{ fontSize: '14px' }}>{agent.Status || "N/A"}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <div className="text-end py-3">
                <Pagination
                  current={currentPage}
                  total={filteredPhoneAgents.length || 50}
                  pageSize={10}
                  onChange={onPageChange}
                />
              </div>
              </>
            )}

            {activeTab === "Activity Log" && (
              <div className="w-100 m-auto mt-4 overflow-auto px-2 pb-5">
                {filteredActivityLogs.map((activity) => {
                  const isOpen = expandedLogs.includes(activity.dateStr);

                  return (
                    <div key={activity.id} className="d-flex align-items-stretch justify-content-between gap-4 mb-4">
                      <div className="fw-semibold text-end position-relative" style={{ width: '25%', top: '12px', fontSize: '14px' }}>
                        {activity.dateStr}
                      </div>

                      <div className="but-cover" style={{ width: '8.333333%' }}>
                        <div className="but cursor-pointer" onClick={() => toggleLog(activity.dateStr)}>
                          {isOpen ? <BiMinus size={20} className="text-muted" /> : <BiPlus size={20} className="text-muted" />}
                        </div>
                      </div>

                      {!isOpen ? (
                        <div className="bg-light py-3 px-4 rounded-3 cursor-pointer" style={{ width: '75%', cursor: 'pointer' }} onClick={() => toggleLog(activity.dateStr)}>
                          <div className="fw-semibold text-dark" style={{ fontSize: '14px' }}>Click here to expand and view activity log for {activity.dateStr}</div>
                          <div className="text-muted py-1" style={{ fontSize: '12px' }}>{activity.secondaryDateStr}</div>
                        </div>
                      ) : (
                        <div className="bg-light py-3 px-4 rounded-3 mt-5" style={{ width: '75%' }}>
                          {activity.logs.map((log, index) => (
                            <div key={log.id} className="position-relative">
                              <div style={{ width: '16.666667%' }}>
                                <div className="child-but-cover">
                                  <BiBell size={20} className="text-muted" />
                                </div>
                              </div>
                              <div className={index === activity.logs.length - 1 ? "pb-0" : "pb-4"}>
                                <div className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                                  {log.type}: <span className="text-primary fw-bold">{log.user}</span> {log.action}
                                </div>
                                <div className="text-muted py-1" style={{ fontSize: '12px' }}>{log.time}</div>
                                {index !== activity.logs.length - 1 && (
                                  <div className="border-start ps-3 pt-2" style={{ borderColor: '#E1E1E1' }}></div>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {activeTab !== "Transaction History" && activeTab !== "Support Agents" && activeTab !== "Phone Agents" && activeTab !== "Activity Log" && (
              <div className="py-5 text-center text-muted">
                {activeTab} Content Area
              </div>
            )}
          </div>
        </div>
      </Layout>
      <InviteEmployeeModal 
        show={showInviteModal} 
        onHide={() => setShowInviteModal(false)} 
        subdomain={subdomain || selectedEnterprise?.tenantId || ""}
      />
    </div>
  );
};

export default CompanyDetails;

